// UI-side help text + live-state read-out for card effects. The short
// `CardEffectInstance.text` lives on the card face; this module adds the
// verbose explanation shown in the card-detail popup, plus the live counter
// badges (fuse / banked / rounds held / frozen) read from `state`.
import type { CardData } from '../game/types';
import type { CardEffectInstance, EffectKind } from '../game/effects';

// Verbose, one-paragraph explanation per effect kind.
const KIND_HELP: Record<EffectKind, string> = {
  give_resources:
    'When you play this card you immediately gain resources of the shown suit.',
  take_only_singleton:
    'This card can only be captured by an exact value match — it can never be pulled in as part of a multi-card sum.',
  double_resources_when_taken:
    'If an opponent captures this card, the thief collects double its value in resources.',
  double_resources_when_played:
    'Playing this card pays out double its value in resources, whether or not it captures anything.',
  double_resources_when_capturing:
    'If playing this card captures something, you collect double its value in resources.',
  double_resources_when_acquired:
    'Whoever ends up owning this card — you by capturing, the opponent by stealing it — collects double its value.',
  steal_resources:
    'When you play this card you steal resources of its suit from the opponent (nothing happens if they have none).',
  decaying_value:
    'Perishable. Every round this card sits on a dropzone it loses 1 value (never below 1) — capture it before it rots.',
  time_bomb:
    'Armed. Every round on a dropzone the fuse drops by 1. At zero it detonates and destroys cards on that dropzone — the bomb itself survives.',
  freeze:
    'When laid on a dropzone this card freezes another card there: a frozen card cannot be captured until the lock expires.',
  ripening_loot:
    'Appreciating. Every round on a dropzone it banks extra resources; whoever captures it collects the whole banked pile on top of its value.',
  held_charge:
    'Slow burn. The longer it waits in your hand, the bigger the resource payout when you finally play it.',
  stash_payout:
    'A Table card. If it is still sitting on a dropzone when Hand 2 is dealt, you collect a bonus of its suit. Protect it for the first four rounds.',
  sleeper:
    'A Hand 2 card. When it arrives with your second hand it gains +1 value for the rest of the match.',
  seize_priority:
    'When laid on a dropzone without capturing, you get priority there next round. Priorities are always opposite, so you give it up on the other dropzone.',
  getaway:
    'At the end of every round, if this card is on a dropzone, it moves to the other one. Hard to pin down, easy to surprise with.',
};

// Verbose explanation for one effect instance (falls back to the short text).
export function describeEffect(effect: CardEffectInstance): string {
  return KIND_HELP[effect.kind] ?? effect.text;
}

// A live counter to surface on the card face and in the popup.
export interface CardStateBadge {
  icon: string;
  value: number;
  title: string;  // full sentence for tooltip / popup status line
}

// Reads the live stateful counters off a card: frozen lock, bomb fuse,
// ripening bank, rounds held. Returns only the badges currently meaningful.
export function cardStateBadges(card: CardData): CardStateBadge[] {
  const out: CardStateBadge[] = [];

  if (card.frozenTurns && card.frozenTurns > 0) {
    out.push({
      icon: '❄', // ❄
      value: card.frozenTurns,
      title: `Frozen — cannot be captured for ${card.frozenTurns} more round${card.frozenTurns === 1 ? '' : 's'}`,
    });
  }

  for (const e of card.effects ?? []) {
    const st = e.state;
    if (!st) continue;
    if (e.kind === 'time_bomb' && typeof st.fuse === 'number' && !st.detonated) {
      out.push({
        icon: '💣', // 💣
        value: st.fuse,
        title: `Time bomb — detonates in ${st.fuse} round${st.fuse === 1 ? '' : 's'} on a dropzone`,
      });
    }
    if (e.kind === 'ripening_loot' && typeof st.banked === 'number' && st.banked > 0) {
      out.push({
        icon: '📈', // 📈
        value: st.banked,
        title: `Ripening — ${st.banked} resource${st.banked === 1 ? '' : 's'} banked, collected by whoever captures it`,
      });
    }
    if (e.kind === 'held_charge' && typeof st.roundsHeld === 'number' && st.roundsHeld > 0) {
      out.push({
        icon: '⏳', // ⏳
        value: st.roundsHeld,
        title: `Slow burn — held ${st.roundsHeld} round${st.roundsHeld === 1 ? '' : 's'}, bigger payout when played`,
      });
    }
  }

  return out;
}
